import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DetailPageLayout } from '@app/DetailPageLayout';
import {
  Checkbox,
  ComponentType,
  Form,
  FormTextField,
  Grid,
  IOption,
  LoadingButton,
  convertDateToStringIgnoringTimezone,
  convertISOStringToLocalDateString,
  requiredRule,
  useForm,
} from '@gcl/react-core';
import { UseMutationResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosErrorResponse } from '@core/error.models';
import './AdminPage.scss';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { IAdminPageFieldConfig, IAdminPageFieldConfigImage, IAdminPageFieldConfigSelect } from './admin.models';
import { EAdminPageFieldType, EAdminPageFormMode, EAdminPageMainFormKey, adminPageList } from './admin.constants';
import { AdminPageField } from './AdminPageField';

export const AdminPage = () => {
  const { navigate } = useNavigate();
  const queryClient = useQueryClient();

  const idUseFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });

  const itemUseFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });

  const { type: adminPageType } = useParams();
  const pageConfig = useMemo(() => {
    return adminPageList.find((item) => item.pageType === adminPageType);
  }, [adminPageType]);

  const [itemId, setItemId] = useState<number | undefined>();
  const [idFormRequest, setIdFormRequest] = useState<object | undefined>();
  const [mode, setMode] = useState<EAdminPageFormMode>(EAdminPageFormMode.UPDATE);

  useEffect(() => {
    if (!pageConfig?.enableUpdateWithId) setMode(EAdminPageFormMode.ADD);
    resetFetchMutation();
    resetAdd();
    resetUpdate();
    setItemId(undefined);
    idUseFormMethods.reset({});
    itemUseFormMethods.reset({});
  }, [pageConfig]);

  useEffect(() => {
    idUseFormMethods.reset({});
    itemUseFormMethods.reset({});
  }, [mode]);

  const idFieldFormKey = useMemo(() => pageConfig?.idFieldFormKey ?? EAdminPageMainFormKey.ID, [pageConfig]);

  const fetchFn = useMemo(() => {
    if (pageConfig?.fetchFn) return pageConfig.fetchFn;
    return (id?: number, enable?: boolean, props?: any) =>
      useQuery<any | undefined, AxiosErrorResponse>([id], async () => undefined, {
        enabled: false,
      });
  }, [pageConfig]);
  const fetchMutationFn: () => UseMutationResult<any, AxiosErrorResponse, any> = useMemo(() => {
    if (pageConfig?.fetchMutationFn) return pageConfig.fetchMutationFn;
    return () => useMutation<any, AxiosErrorResponse>([]);
  }, [pageConfig]);
  const updateFn: () => UseMutationResult<any, AxiosErrorResponse, any> = useMemo(() => {
    if (pageConfig?.updateFn) return pageConfig.updateFn;
    return () => useMutation<any, AxiosErrorResponse>([]);
  }, [pageConfig]);

  const addFn: () => UseMutationResult<any, AxiosErrorResponse, any> = useMemo(() => {
    if (pageConfig?.addFn) return pageConfig.addFn;
    return () => useMutation<any, AxiosErrorResponse>([]);
  }, [pageConfig]);

  const [optionData, setOptionData] = useState<{ [key: string]: any }>({});

  const {
    isInitialLoading: isInitialLoadingItem,
    isRefetching: isRefetchingItem,
    isSuccess: isSuccessItem,
    isError: isErrorItem,
    error: errorItem,
    data: dataItem,
  } = fetchFn(itemId, true, idFormRequest);

  const {
    isLoading: isLoadingFetchMutation,
    isError: isErrorFetchMutation,
    error: errorFetchMutation,
    isSuccess: isSuccessFetchMutation,
    mutate: mutateFetchMutation,
    reset: resetFetchMutation,
    data: dataFetchMutation,
  } = fetchMutationFn();

  const item = useMemo(() => dataItem ?? dataFetchMutation, [dataItem, dataFetchMutation]);

  const {
    isLoading: isLoadingUpdate,
    isError: isErrorUpdate,
    error: errorUpdate,
    isSuccess: isSuccessUpdate,
    mutate: mutateUpdate,
    reset: resetUpdate,
  } = updateFn();

  const {
    isLoading: isLoadingAdd,
    isError: isErrorAdd,
    error: errorAdd,
    isSuccess: isSuccessAdd,
    mutate: mutateAdd,
    reset: resetAdd,
    data: dataAdd,
  } = addFn();

  const onSubmitItem = useCallback(
    (type: 'update' | 'add') => (data: { [x: string]: any }) => {
      const convertedData: { [x: string]: any } = {};

      pageConfig?.fieldConfigs.map((config) => {
        if (config.convertFn) {
          convertedData[config.fieldName] = config.convertFn(data[config.fieldName]);
          return;
        }
        if (
          config.fieldType === EAdminPageFieldType.CHECKBOX_GROUP &&
          (config as IAdminPageFieldConfigSelect).optionGetProp?.shouldConvertOptions
        ) {
          convertedData[config.fieldName] = (data[config.fieldName] as IOption<any>[]).map((option) => option.data);
          return;
        }
        if (config.fieldType === EAdminPageFieldType.DATE) {
          convertedData[config.fieldName] = data[config.fieldName]
            ? convertDateToStringIgnoringTimezone(new Date(data[config.fieldName]))
            : data[config.fieldName];
        }
        if (config.fieldType === EAdminPageFieldType.IMAGE) {
          convertedData[config.fieldName] = data[config.fieldName].files?.[0];
          convertedData[(config as IAdminPageFieldConfigImage).fileNameFieldName] =
            data[config.fieldName].files?.[0]?.name;
        }
      });
      if (type === 'add') {
        mutateAdd({ ...data, ...convertedData });
        return;
      }
      if (type === 'update') {
        const newItem = { ...item, ...data, ...convertedData };
        pageConfig?.dropFields?.map((field) => {
          delete newItem[field];
        });
        mutateUpdate(newItem);
      }
    },
    [JSON.stringify(item), JSON.stringify(pageConfig)],
  );

  const onSubmitId = useCallback(
    (data: { [x: string]: any }) => {
      itemUseFormMethods.reset();
      setItemId(data[idFieldFormKey]);
      setIdFormRequest(data);
      if (pageConfig?.fetchMutationFn) {
        mutateFetchMutation({ [idFieldFormKey]: data[idFieldFormKey] });
      }
    },
    [idFieldFormKey, pageConfig],
  );

  const getDefaultFormValues = (
    itemLocal: { [key: string]: any },
    configLocal: IAdminPageFieldConfig[],
    optionDataLocal: { [key: string]: Array<IOption<any> | any[]> },
  ) => {
    const state: { [key: string]: any } = { ...itemLocal };

    configLocal.map((c) => {
      const dataFieldName = c.dataFieldName ?? c.fieldName;
      if (c.fieldName.length <= 0) return;
      const fieldData = itemLocal?.[dataFieldName];
      if (!fieldData) return;
      if (c.fieldType === EAdminPageFieldType.CHECKBOX_GROUP && Array.isArray(fieldData)) {
        const selectConfig = c as IAdminPageFieldConfigSelect;
        state[c.fieldName] = fieldData.map((d) => {
          if (selectConfig.optionGetProp?.shouldConvertOptions)
            return optionDataLocal[c.fieldName]?.find(
              (option) =>
                (option as IOption<any>).key === d[(selectConfig.optionIdFieldName ?? 'id') as keyof typeof d],
            );
          return optionDataLocal[selectConfig.fieldName].find(
            (option) =>
              (option as object)[(selectConfig.optionIdFieldName ?? 'id') as keyof typeof option] ===
              d[selectConfig.optionIdFieldName ?? 'id'],
          );
        });
        return;
      }
      if (c.fieldType === EAdminPageFieldType.DATE) {
        state[c.fieldName] = convertISOStringToLocalDateString(fieldData ?? '');
        return;
      }

      state[c.fieldName] = fieldData;
    });
    return state;
  };

  return (
    <DetailPageLayout
      errorGetDetail={null}
      isDrawer={false}
      isRestricted={false}
      data="placeholder"
      isLoading={false}
      isRefetching={false}
      title={pageConfig?.pageName}
      breadcrumb={[
        {
          text: 'Home',
          prefixIconName: 'House',
          onClick: () => {
            navigate(routes.home.path);
          },
        },
        {
          text: pageConfig?.pageName,
          prefixIconName: 'People',
          active: true,
        },
      ]}
      render={() => {
        return (
          <>
            <ApiSuccessToast
              successes={[
                { isSuccess: isSuccessUpdate, reset: resetUpdate, message: 'Update Success' },
                { isSuccess: isSuccessAdd, reset: resetAdd, message: 'Create Success' },
              ]}
              callbackAfterToasted={() => {
                if (isSuccessAdd) {
                  pageConfig?.addSuccessCallback?.(dataAdd, idUseFormMethods, itemUseFormMethods);
                  if (pageConfig?.enableUpdateWithId && !!dataAdd.id) {
                    setMode(EAdminPageFormMode.UPDATE);
                    setItemId(dataAdd.id);
                    idUseFormMethods.setValue(EAdminPageMainFormKey.ID, dataAdd.id);
                  }
                }
                if (isSuccessUpdate) {
                  itemUseFormMethods.reset();
                  queryClient.invalidateQueries([pageConfig?.fetchQueryKey, itemId]);
                }
              }}
            />
            <ApiErrorDisplay
              errors={[
                { error: errorUpdate, reset: resetUpdate, genericError: 'Update Failed' },
                { error: errorAdd, reset: resetAdd, genericError: 'Create Failed' },
                { error: errorItem, genericError: 'Get item failed' },
                { error: errorFetchMutation, reset: resetFetchMutation, genericError: 'Get item failed' },
              ]}
            />
            {pageConfig?.headerComponent != null ? pageConfig.headerComponent : null}
            <Form
              className="gcl-gems-admin-id-container"
              useFormMethods={idUseFormMethods}
              render={(idFormFieldMethods) => {
                return (
                  <Grid container spacing={2}>
                    {pageConfig?.enableAdd && pageConfig.enableUpdateWithId && (
                      <Grid item xs={12}>
                        <LabelWithMessage labelFor={EAdminPageMainFormKey.IS_ADD} text="Is Create?" />
                        <Checkbox
                          id={EAdminPageMainFormKey.IS_ADD}
                          checked={mode === EAdminPageFormMode.ADD}
                          onChange={(checked: boolean) => {
                            setMode(checked ? EAdminPageFormMode.ADD : EAdminPageFormMode.UPDATE);
                          }}
                        />
                      </Grid>
                    )}
                    {mode === EAdminPageFormMode.UPDATE && pageConfig?.enableUpdateWithId && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <LabelWithMessage labelFor={idFieldFormKey} text={pageConfig.idFieldLabel ?? 'ID'} required />
                          <FormTextField
                            rules={requiredRule}
                            id={idFieldFormKey}
                            formFieldMethods={idFormFieldMethods}
                            className="gcl-mr-sm"
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          sx={{
                            display: { xs: 'block', sm: 'flex' },
                            alignItems: { xs: 'flex-start', sm: 'flex-end' },
                          }}
                        >
                          <LoadingButton
                            type={ComponentType.Primary}
                            onClick={() => {
                              idUseFormMethods.handleSubmit(onSubmitId)();
                            }}
                            isLoading={isInitialLoadingItem || isRefetchingItem || isLoadingFetchMutation}
                            isSuccess={isSuccessItem || isSuccessFetchMutation}
                            isError={isErrorItem || isErrorFetchMutation}
                          >
                            Get
                          </LoadingButton>
                        </Grid>
                        {pageConfig?.fetchFormExtraFields?.map((field) => {
                          return (
                            <Grid item xs={12} sm={6} key={field.fieldName}>
                              <AdminPageField
                                dataItem={dataItem}
                                key={field.fieldName}
                                pageMode={mode}
                                config={field}
                                itemFormFieldMethods={idUseFormMethods}
                                idUseFormMethods={idUseFormMethods}
                                setOptionData={setOptionData}
                              />
                            </Grid>
                          );
                        })}
                      </>
                    )}
                  </Grid>
                );
              }}
            />
            {(!!item || pageConfig?.enableAdd) && (
              <Form
                defaultValues={{
                  callback: () => getDefaultFormValues(item, pageConfig?.fieldConfigs ?? [], optionData),
                  deps: [item, pageConfig?.fieldConfigs, optionData],
                }}
                useFormMethods={itemUseFormMethods}
                render={(itemFormFieldMethods) => {
                  if (mode === EAdminPageFormMode.UPDATE && !item) return <></>;

                  return (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        {mode === EAdminPageFormMode.UPDATE &&
                          pageConfig?.enableUpdateWithId &&
                          pageConfig.updateFn &&
                          !!item && (
                            <LoadingButton
                              type={ComponentType.Primary}
                              onClick={() => {
                                itemUseFormMethods.handleSubmit(onSubmitItem('update'))();
                              }}
                              isLoading={isLoadingUpdate}
                              isSuccess={isSuccessUpdate}
                              isError={isErrorUpdate}
                            >
                              Update
                            </LoadingButton>
                          )}
                        {mode === EAdminPageFormMode.ADD && pageConfig?.enableAdd && (
                          <LoadingButton
                            type={ComponentType.Primary}
                            onClick={() => {
                              itemUseFormMethods.handleSubmit(onSubmitItem('add'))();
                            }}
                            isLoading={isLoadingAdd}
                            isSuccess={isSuccessAdd}
                            isError={isErrorAdd}
                          >
                            Create
                          </LoadingButton>
                        )}
                      </Grid>
                      {pageConfig?.fieldConfigs.map((config) => {
                        return (
                          <AdminPageField
                            dataItem={dataItem}
                            key={config.fieldName}
                            pageMode={mode}
                            config={config}
                            itemFormFieldMethods={itemFormFieldMethods}
                            idUseFormMethods={idUseFormMethods}
                            setOptionData={setOptionData}
                          />
                        );
                      })}
                    </Grid>
                  );
                }}
              />
            )}
          </>
        );
      }}
    />
  );
};
